import { useState, useEffect, useCallback } from "react";
import api from "../api";

var POLL_INTERVAL = 200;
var POLL_TIMEOUT = 10000;

function useMusicKit() {
  var authorizedState = useState(false);
  var isAuthorized = authorizedState[0];
  var setIsAuthorized = authorizedState[1];

  var loadingState = useState(true);
  var isLoading = loadingState[0];
  var setIsLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var instanceState = useState(null);
  var musicKitInstance = instanceState[0];
  var setMusicKitInstance = instanceState[1];

  useEffect(function () {
    var cancelled = false;

    function init() {
      api
        .get("/api/music/apple/developer-token")
        .then(function (result) {
          var developerToken = result.data.token;
          return waitForMusicKit().then(function () {
            return developerToken;
          });
        })
        .then(function (developerToken) {
          if (cancelled) return;
          var result = window.MusicKit.configure({
            developerToken: developerToken,
            app: {
              name: "HeavyRotation",
              build: "1.0",
            },
          });
          // MusicKit v3 configure may return a promise or the instance directly
          return Promise.resolve(result);
        })
        .then(function () {
          if (cancelled) return;
          var instance = window.MusicKit.getInstance();
          setMusicKitInstance(instance);
          setIsAuthorized(instance.isAuthorized);
          setIsLoading(false);
        })
        .catch(function (err) {
          if (cancelled) return;
          setError(err.message || "Failed to initialize MusicKit");
          setIsLoading(false);
        });
    }

    function waitForMusicKit() {
      return new Promise(function (resolve, reject) {
        if (window.MusicKit) {
          resolve();
          return;
        }
        var elapsed = 0;
        var interval = setInterval(function () {
          elapsed += POLL_INTERVAL;
          if (window.MusicKit) {
            clearInterval(interval);
            resolve();
          } else if (elapsed >= POLL_TIMEOUT) {
            clearInterval(interval);
            reject(new Error("MusicKit JS failed to load"));
          }
        }, POLL_INTERVAL);
      });
    }

    init();

    return function () {
      cancelled = true;
    };
  }, []);

  var authorize = useCallback(
    function () {
      if (!musicKitInstance) return Promise.reject(new Error("MusicKit not ready"));
      return musicKitInstance
        .authorize()
        .then(function (musicUserToken) {
          setIsAuthorized(true);
          return api.post("/api/music/connect", {
            provider: "apple_music",
            musicUserToken: musicUserToken,
          });
        })
        .catch(function (err) {
          setError(err.message || "Authorization failed");
          throw err;
        });
    },
    [musicKitInstance]
  );

  var unauthorize = useCallback(
    function () {
      if (!musicKitInstance) return Promise.reject(new Error("MusicKit not ready"));
      return musicKitInstance
        .unauthorize()
        .then(function () {
          setIsAuthorized(false);
          return api.delete("/api/music/connect/apple_music");
        })
        .catch(function (err) {
          setError(err.message || "Failed to disconnect");
          throw err;
        });
    },
    [musicKitInstance]
  );

  return {
    isAuthorized: isAuthorized,
    isLoading: isLoading,
    error: error,
    authorize: authorize,
    unauthorize: unauthorize,
  };
}

export default useMusicKit;
